/**
 * Start a dedicated agent-panel chat for Basecamp todo ACTIONS
 * (draft comment, find context, etc.) without clobbering an open ingest thread.
 */

function truncateTitle(title: string, maxLen = 56): string {
  const trimmed = title.trim()
  if (trimmed.length <= maxLen) return trimmed
  return `${trimmed.slice(0, maxLen - 3).trim()}...`
}

export type BasecampTodoActionKind = 'draft_comment' | 'find_context'

export type BeginBasecampTodoActionChatOptions = {
  kind: BasecampTodoActionKind
  todoId: string
  todoTitle: string
  /** Document tab id for context chip (defaults to basecamp-todo-{todoId}). */
  contextDocumentId?: string
}

const THREAD_TITLE_PREFIX: Record<BasecampTodoActionKind, string> = {
  draft_comment: 'Draft comment',
  find_context: 'Find context',
}

/** Create a fresh agent-panel chat and return the thread id for event routing. */
export function beginBasecampTodoActionChat(
  options: BeginBasecampTodoActionChatOptions,
): string {
  const threadId = `new-${Date.now()}`
  const contextDocumentId = options.contextDocumentId ?? `basecamp-todo-${options.todoId}`
  const prefix = THREAD_TITLE_PREFIX[options.kind]

  window.dispatchEvent(
    new CustomEvent('chat-create-new', {
      detail: {
        threadId,
        forceAgentPanel: true,
        preferNew: true,
        contextDocumentId,
        contextDocumentTitle: options.todoTitle,
        threadTitle: truncateTitle(`${prefix}: ${options.todoTitle}`),
      },
    }),
  )

  window.dispatchEvent(
    new CustomEvent('chat-set-mode', {
      detail: { mode: 'plan', threadId },
    }),
  )

  return threadId
}

export type TodoActionChatSession = {
  threadId: string
  kind: BasecampTodoActionKind
  todoId: string
}

let activeTodoActionSession: TodoActionChatSession | null = null

export function setActiveTodoActionSession(session: TodoActionChatSession | null): void {
  activeTodoActionSession = session
}

export function getActiveTodoActionSession(): TodoActionChatSession | null {
  return activeTodoActionSession
}

export function migrateTodoActionSessionThread(fromThreadId: string, toThreadId: string): void {
  if (!activeTodoActionSession || activeTodoActionSession.threadId !== fromThreadId) return
  activeTodoActionSession = { ...activeTodoActionSession, threadId: toThreadId }
}

const DRAFT_BODY_MARKERS = [
  /here is the draft comment:\s*/i,
  /draft comment:\s*/i,
  /proposed comment:\s*/i,
  /comment body:\s*/i,
]

/**
 * Pull the postable comment body out of an assistant message.
 * Drops shaping preamble, "here is the draft" labels, and markdown fences.
 */
export function extractPlainCommentDraft(assistantText: string): string {
  let text = assistantText.trim()
  if (!text) return ''

  const fences = [...text.matchAll(/```(?:comment|text|markdown|plaintext)?\s*\n([\s\S]*?)```/gi)]
  if (fences.length > 0) {
    text = (fences[fences.length - 1][1] || '').trim()
  } else {
    for (const re of DRAFT_BODY_MARKERS) {
      const match = text.match(re)
      if (match && match.index != null) {
        text = text.slice(match.index + match[0].length).trim()
        break
      }
    }
  }

  text = text.replace(/^---+\s*/g, '').trim()
  return text
}

export const FILL_COMMENT_FORM_CHOICE = {
  id: 'fill-comment-form',
  label: 'Add to comments form',
  description: 'Copy the comment body into Maater Comments (does not post)',
  action: 'fill_comment_form' as const,
  variant: 'primary' as const,
}

type ChoiceLike = {
  id?: string
  action?: string
}

/** Attach the fill-form CTA unless the model is still waiting on askQuestion. */
export function attachFillCommentChoice<T extends ChoiceLike>(
  existing: T[] | undefined,
  askUnanswered: boolean,
): T[] | undefined {
  if (askUnanswered) return existing
  const list = (existing ? [...existing] : []) as T[]
  if (list.some((c) => c.action === 'fill_comment_form' || c.id === 'fill-comment-form')) {
    return list
  }
  return [...list, FILL_COMMENT_FORM_CHOICE as unknown as T]
}
